import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, MapPin, Target, Users, Palette, Flag, ChevronRight, ChevronLeft, Check, X, Loader2, Circle, Phone, Mail, MapPinned, Image as ImageIcon, Upload, TrendingUp, Megaphone, Hammer, Link2, Sparkles, Lightbulb, EyeOff, Eye, Play } from 'lucide-react';
import { apiClient, API_BASE_URL, skipQuickstartStep } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import PairingWizard from '../WordPress/PairingWizard';
import ContentPlanWizard from '../SEO/ContentPlanWizard';
import './OnboardingWizard.css';

type Step = 1 | 2 | 3 | 4 | 5 | 6;

interface LogoVariants {
  primary: string;
  dark_bg: string;
  mono_black: string;
  mono_white: string;
  favicon: string | null;
}

interface LaunchEvent {
  step: string;
  status: string;
  message?: string;
  index?: number;
  total?: number;
  title?: string;
  project_id?: string;
  // logo preview fields
  preview_id?: string;
  brand_guide_id?: string;
  variants?: LogoVariants;
}

const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional', desc: 'Formal, trustworthy' },
  { value: 'friendly',     label: 'Friendly',     desc: 'Warm, approachable' },
  { value: 'bold',         label: 'Bold',         desc: 'Confident, direct' },
  { value: 'creative',     label: 'Creative',     desc: 'Playful, imaginative' },
  { value: 'minimal',      label: 'Minimal',      desc: 'Clean, concise' },
];

const GOAL_OPTIONS = [
  { value: 'traffic',   label: 'Drive Traffic',  desc: 'SEO articles & blog posts', icon: <TrendingUp size={28} /> },
  { value: 'leads',     label: 'Generate Leads', desc: 'Landing pages & forms',     icon: <Target size={28} /> },
  { value: 'promotion', label: 'Run Promotions', desc: 'Social posts & emails',     icon: <Megaphone size={28} /> },
];

const INDUSTRY_OPTIONS = [
  'Real Estate', 'Health & Wellness', 'Finance & Accounting', 'Legal Services',
  'Retail & eCommerce', 'Hospitality & Food', 'Technology & SaaS',
  'Education & Training', 'Construction & Trades', 'Marketing & Creative',
  'Other',
];

const LOGO_DESIGN_STYLES = [
  { value: 'modern', label: 'Modern', desc: 'Clean, contemporary' },
  { value: 'classic', label: 'Classic', desc: 'Timeless, traditional' },
  { value: 'playful', label: 'Playful', desc: 'Fun, energetic' },
  { value: 'elegant', label: 'Elegant', desc: 'Refined, luxury' },
  { value: 'bold', label: 'Bold', desc: 'Strong, impactful' },
];

const LOGO_COMPLEXITY = [
  { value: 'minimal', label: 'Minimal', desc: 'Icon only' },
  { value: 'wordmark', label: 'Wordmark', desc: 'Text only' },
  { value: 'combination', label: 'Combination', desc: 'Icon + text' },
];

const LOGO_SHAPES = [
  { value: 'angular', label: 'Angular', desc: 'Sharp, structured' },
  { value: 'rounded', label: 'Rounded', desc: 'Soft, friendly' },
  { value: 'geometric', label: 'Geometric', desc: 'Shapes, symmetry' },
  { value: 'organic', label: 'Organic', desc: 'Natural, flowing' },
];

const LOGO_COLOR_MODES = [
  { value: 'full_colour', label: 'Full Colour', desc: 'Brand palette' },
  { value: 'monochrome', label: 'Monochrome', desc: 'Single colour' },
  { value: 'two_tone', label: 'Two-Tone', desc: 'Primary + accent' },
];

// ── Onboarding tutorial — helper text + YouTube video timestamps ─────────────
//
// One unlisted YouTube video walks new users through every wizard field.
// On focus, the sidecar player seeks to that field's timestamp.
//
// When the video is re-recorded and replaced (using YouTube's "Edit > Replace
// video" feature to preserve the URL/ID), update the seconds below. Field keys
// are stable — only the numbers change.
const TUTORIAL_VIDEO_ID = 'YOUR_VIDEO_ID_HERE'; // ← replace with unlisted YouTube video ID
const TUTORIAL_FIELDS = [
  'industry',
  'location',
  'targetCustomer',
  'competitors',
  'companyContact',
  'brandName',
  'brandDescription',
  'brandColors',
  'logo',
  'websiteChoice',
  'primaryGoal',
] as const;
type TutorialFieldKey = typeof TUTORIAL_FIELDS[number];

const TUTORIAL_TIMESTAMPS: Record<TutorialFieldKey, number> = {
  industry: 0,
  location: 14,
  targetCustomer: 28,
  competitors: 50,
  companyContact: 70,
  brandName: 90,
  brandDescription: 105,
  brandColors: 135,
  logo: 155,
  websiteChoice: 185,
  primaryGoal: 210,
};

// On-focus helper text shown beneath the active field. Plain language, no jargon.
const TUTORIAL_HELP: Record<TutorialFieldKey, { title: string; body: string }> = {
  industry: {
    title: 'Why we ask',
    body: 'AiMe tailors your content tone, examples, and references to your industry. Pick the closest match — you can always adjust later.',
  },
  location: {
    title: 'Why we ask',
    body: 'AiMe uses your location for local SEO, currency, and regional context in your content. Example: "Sydney, Australia".',
  },
  targetCustomer: {
    title: 'Who buys from you?',
    body: 'A short description of your ideal customer. Example: "Solo accountants and small accounting firms in regional Australia with 5–20 staff". The more specific, the better AiMe\'s content will resonate.',
  },
  competitors: {
    title: 'Who else is in this space?',
    body: 'Up to 3 real competitor brands. AiMe uses these to differentiate your messaging — not to copy them. Press Enter after each name.',
  },
  companyContact: {
    title: 'Used on your assets',
    body: 'Your contact details appear in email footers, landing pages, and website pages AiMe builds for you. Leave blank if you\'re not sure yet.',
  },
  brandName: {
    title: 'The name on every asset',
    body: 'Your business or brand name as you want it to appear publicly. AiMe uses it in articles, social posts, email signatures, and your landing pages.',
  },
  brandDescription: {
    title: 'Your brand in a paragraph',
    body: 'What you do, who you serve, and how you sound. Example: "We help solo accountants reclaim 5 hours a week by automating their bookkeeping intake. Friendly, practical, jargon-free." Use ✨ Suggest if you\'re stuck.',
  },
  brandColors: {
    title: 'Your brand palette',
    body: 'Primary is your main brand colour — used for buttons and headings. Secondary supports it. AiMe applies both to every asset it generates.',
  },
  logo: {
    title: 'Got a logo? Drop it here',
    body: 'Upload a logo and AiMe extracts your brand colours from it. No logo? Pick a style below and AiMe will generate one for you.',
  },
  websiteChoice: {
    title: 'Already have a site?',
    body: 'Connect an existing WordPress site to publish directly from AiMe. Starting fresh? AiMe builds you a new site from your brand guide.',
  },
  primaryGoal: {
    title: 'What matters most right now?',
    body: 'Your primary goal shapes the first batch of content AiMe generates. You can run all three goals concurrently later — this just sets the starting focus.',
  },
};

// YouTube IFrame Player API loader — runs once, idempotent.
let _ytApiLoading: Promise<any> | null = null;
function loadYouTubeApi(): Promise<any> {
  if (_ytApiLoading) return _ytApiLoading;
  if ((window as any).YT && (window as any).YT.Player) {
    _ytApiLoading = Promise.resolve((window as any).YT);
    return _ytApiLoading;
  }
  _ytApiLoading = new Promise(resolve => {
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
    const prev = (window as any).onYouTubeIframeAPIReady;
    (window as any).onYouTubeIframeAPIReady = () => {
      if (prev) try { prev(); } catch {}
      resolve((window as any).YT);
    };
  });
  return _ytApiLoading;
}

// Tutorial sidecar — YouTube embed + dismiss + placeholder fallback.
// On focused-field change, seeks the player to the matching timestamp.
const TutorialSidecar: React.FC<{
  activeField: TutorialFieldKey | null;
  onDismiss: () => void;
}> = ({ activeField, onDismiss }) => {
  const containerId = 'aime-onboarding-tutorial-player';
  const [player, setPlayer] = useState<any>(null);
  const [playerReady, setPlayerReady] = useState(false);
  const isPlaceholder = !TUTORIAL_VIDEO_ID || TUTORIAL_VIDEO_ID === 'YOUR_VIDEO_ID_HERE';

  // Initialise the YouTube player once the API is available.
  useEffect(() => {
    if (isPlaceholder) return;
    let cancelled = false;
    let createdPlayer: any = null;
    loadYouTubeApi().then(YT => {
      if (cancelled) return;
      createdPlayer = new YT.Player(containerId, {
        host: 'https://www.youtube-nocookie.com',
        videoId: TUTORIAL_VIDEO_ID,
        playerVars: {
          autoplay: 0,
          controls: 0,
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
          enablejsapi: 1,
          disablekb: 1,
          fs: 0,
          iv_load_policy: 3,
          origin: window.location.origin,
        },
        events: {
          onReady: (e: any) => {
            e.target.mute();
            setPlayer(e.target);
            setPlayerReady(true);
          },
        },
      });
    });
    return () => {
      cancelled = true;
      if (createdPlayer && typeof createdPlayer.destroy === 'function') {
        try { createdPlayer.destroy(); } catch {}
      }
    };
  }, [isPlaceholder]);

  // Seek to the field's timestamp when focus changes.
  useEffect(() => {
    if (!player || !playerReady || !activeField) return;
    const ts = TUTORIAL_TIMESTAMPS[activeField] ?? 0;
    try {
      player.seekTo(ts, true);
      player.playVideo();
    } catch {}
  }, [activeField, player, playerReady]);

  const help = activeField ? TUTORIAL_HELP[activeField] : null;

  return (
    <aside className="onboarding-tutorial">
      <div className="onboarding-tutorial-header">
        <div className="onboarding-tutorial-title">
          <Play size={14} fill="currentColor" />
          <span>Tutorial</span>
        </div>
        <button
          type="button"
          className="onboarding-tutorial-dismiss"
          onClick={onDismiss}
          title="Hide tutorial"
        >
          <EyeOff size={14} />
        </button>
      </div>

      <div className="onboarding-tutorial-stage">
        {isPlaceholder ? (
          <div className="onboarding-tutorial-placeholder">
            <div className="onboarding-tutorial-avatar">
              <Users size={42} />
            </div>
            <p className="onboarding-tutorial-placeholder-label">
              Video tutorial coming soon
            </p>
          </div>
        ) : (
          <>
            <div id={containerId} className="onboarding-tutorial-player" />
            {/* Overlay above the iframe to suppress YouTube hover chrome (title, share, etc.). */}
            <div className="onboarding-tutorial-chrome-shield" aria-hidden="true" />
          </>
        )}
      </div>

      {help && (
        <div className="onboarding-tutorial-caption">
          <strong>{help.title}</strong>
          <p>{help.body}</p>
        </div>
      )}
    </aside>
  );
};

// Inline helper text shown beneath the focused field. Renders nothing when the
// field is not active, so the form layout stays compact for the rest.
const FieldHelper: React.FC<{ field: TutorialFieldKey; activeField: TutorialFieldKey | null }> = ({ field, activeField }) => {
  if (activeField !== field) return null;
  const help = TUTORIAL_HELP[field];
  return (
    <div className="onboarding-field-helper">
      <Lightbulb size={12} />
      <div>
        <strong>{help.title}</strong>
        <p>{help.body}</p>
      </div>
    </div>
  );
};

// ✨ Suggest button that calls Gemini to pre-fill the field. User can accept,
// edit, or clear and re-type. Wired for fields where blank input most hurts
// downstream AI output: target customer, competitors, brand description.
const SuggestButton: React.FC<{ loading: boolean; disabled?: boolean; onClick: () => void; label?: string }> = ({ loading, disabled, onClick, label = 'Suggest' }) => (
  <button
    type="button"
    className="onboarding-suggest-btn"
    onClick={onClick}
    disabled={loading || disabled}
    title="Let AiMe suggest an answer based on your other inputs"
  >
    {loading ? <Loader2 size={12} className="spin" /> : <Sparkles size={12} />}
    {label}
  </button>
);

const LAUNCH_STEPS = [
  { key: 'project',        label: 'Creating project' },
  { key: 'brand_guide',    label: 'Generating brand guide' },
  { key: 'logo',           label: 'Creating brand logo' },
  { key: 'email_template', label: 'Setting up email marketing' },
  { key: 'topics',         label: 'Planning article topics' },
  { key: 'article',        label: 'Writing articles',            hasCount: true },
  { key: 'social',         label: 'Creating social media posts', hasCount: true },
  { key: 'calendar',       label: 'Organizing content calendar' },
  { key: 'quiz',           label: 'Building engagement quiz' },
  { key: 'landing_page',   label: 'Creating landing page' },
  { key: 'scheduling',     label: 'Scheduling publications' },
];

const OnboardingWizard: React.FC = () => {
  const navigate = useNavigate();
  const { refreshToken } = useAuth() as any;

  const [step, setStep] = useState<Step>(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Step 1 state
  const [industry, setIndustry] = useState('');
  const [location, setLocation] = useState('');
  const [targetCustomer, setTargetCustomer] = useState('');
  const [competitorInput, setCompetitorInput] = useState('');
  const [competitors, setCompetitors] = useState<string[]>([]);
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');

  // Step 2 state
  const [brandName, setBrandName] = useState('');
  const [brandTone, setBrandTone] = useState('professional');
  const [primaryColor, setPrimaryColor] = useState('#2177FF');
  const [secondaryColor, setSecondaryColor] = useState('#1EACA6');
  const [brandDescription, setBrandDescription] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState('');
  const logoInputRef = useRef<HTMLInputElement>(null);
  // Logo generation style params (shown when no logo uploaded)
  const [logoDesignStyle, setLogoDesignStyle] = useState('modern');
  const [logoComplexity, setLogoComplexity] = useState('combination');
  const [logoShape, setLogoShape] = useState('geometric');
  const [logoColorMode, setLogoColorMode] = useState('full_colour');

  // Step 3 state (website choice)
  const [websiteChoice, setWebsiteChoice] = useState<'build' | 'connect' | 'skip' | ''>('');
  const [showWpPairing, setShowWpPairing] = useState(false);
  const [wpConnectedSite, setWpConnectedSite] = useState<{ site_id: string; site_url: string } | null>(null);

  // Step 4 state (goal)
  const [primaryGoal, setPrimaryGoal] = useState('');

  // Launch state
  const [launching, setLaunching] = useState(false);
  const [launchEvents, setLaunchEvents] = useState<LaunchEvent[]>([]);
  const [launchComplete, setLaunchComplete] = useState(false);
  const [launchProjectId, setLaunchProjectId] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  // Logo approval state (shown during launch when SSE yields a preview event)
  const [logoPreviewEvent, setLogoPreviewEvent] = useState<LaunchEvent | null>(null);
  const [logoDecisionLoading, setLogoDecisionLoading] = useState(false);
  const [logoRegenerating, setLogoRegenerating] = useState(false);
  const logoApprovalRef = useRef<HTMLDivElement>(null);

  // The approval card must never sit unseen below the fold of the scrollable
  // onboarding card — the SSE generator is paused waiting on this decision.
  useEffect(() => {
    if (logoPreviewEvent && !logoRegenerating) {
      logoApprovalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [logoPreviewEvent, logoRegenerating]);

  // Step 5 — Masterplan generation
  const [masterplanCreating, setMasterplanCreating] = useState(false);
  const [masterplanStreams, setMasterplanStreams] = useState<string[]>([]);
  const [masterplanComplete, setMasterplanComplete] = useState(false);
  const [masterplanError, setMasterplanError] = useState<string | null>(null);

  // Step 6 — Content plan wizard (inline)
  const [quickstartApproveSuccess, setQuickstartApproveSuccess] = useState(false);

  // Tutorial sidecar state
  const [focusedField, setFocusedField] = useState<TutorialFieldKey | null>(null);
  const [tutorialVisible, setTutorialVisible] = useState<boolean>(() => {
    const stored = localStorage.getItem('onboarding_tutorial_visible');
    return stored === null ? true : stored === 'true';
  });
  const toggleTutorial = () => {
    setTutorialVisible(v => {
      const next = !v;
      localStorage.setItem('onboarding_tutorial_visible', String(next));
      return next;
    });
  };
  // Per-field AI suggestion loading state
  const [suggestingField, setSuggestingField] = useState<TutorialFieldKey | null>(null);
  const requestSuggestion = async (field: TutorialFieldKey) => {
    setSuggestingField(field);
    try {
      const context: Record<string, string> = {
        industry, location, brand_name: brandName, target_customer: targetCustomer,
      };
      // Map TS field keys → backend field names
      const backendField = field === 'targetCustomer' ? 'target_customer'
        : field === 'brandDescription' ? 'brand_description'
        : field;
      const { data } = await apiClient.post('/onboarding/suggest', { field: backendField, context });
      const suggestion: string = (data?.suggestion || '').trim();
      if (!suggestion) return;
      if (field === 'targetCustomer') setTargetCustomer(suggestion);
      else if (field === 'brandDescription') setBrandDescription(suggestion);
      else if (field === 'competitors') {
        const names = suggestion.split(/,|\n/).map(s => s.trim()).filter(Boolean).slice(0, 3);
        setCompetitors(names);
      }
    } catch (e) {
      // Silent — user can re-click if they want, or type their own answer.
    } finally {
      setSuggestingField(null);
    }
  };

  // Mirror current step to the tutorial for choice screens that have no input focus
  useEffect(() => {
    if (step === 3) setFocusedField('websiteChoice');
    else if (step === 4 && !launching) setFocusedField('primaryGoal');
    // step 1/2 fields manage their own focus via onFocus handlers
  }, [step, launching]);

  // Cleanup EventSource on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  // Logo file preview
  useEffect(() => {
    if (logoFile) {
      const url = URL.createObjectURL(logoFile);
      setLogoPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setLogoPreview('');
  }, [logoFile]);

  const addCompetitor = () => {
    const trimmed = competitorInput.trim();
    if (trimmed && competitors.length < 3 && !competitors.includes(trimmed)) {
      setCompetitors([...competitors, trimmed]);
      setCompetitorInput('');
    }
  };

  const removeCompetitor = (c: string) => setCompetitors(competitors.filter(x => x !== c));

  const saveStep1 = async () => {
    if (!industry || !location || !targetCustomer) {
      setError('Please fill in all required fields');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await apiClient.post('/onboarding/step1', {
        industry, location, target_customer: targetCustomer, competitors,
        company_email: companyEmail, company_phone: companyPhone, company_address: companyAddress,
      });
      setStep(2);
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveStep2 = async () => {
    if (!brandName) {
      setError('Please enter your brand or business name');
      return;
    }
    setSaving(true);
    setError('');
    try {
      // Upload logo if provided
      let logoUrl = '';
      if (logoFile) {
        const formData = new FormData();
        formData.append('file', logoFile);
        const uploadRes = await apiClient.post('/edm/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        logoUrl = uploadRes.data.url;
      }
      await apiClient.post('/onboarding/step2', {
        brand_name: brandName, brand_tone: brandTone,
        primary_color: primaryColor, secondary_color: secondaryColor,
        brand_description: brandDescription, logo_url: logoUrl,
        logo_style: !logoFile ? {
          design_style: logoDesignStyle,
          complexity: logoComplexity,
          shape: logoShape,
          color_mode: logoColorMode,
        } : undefined,
      });
      setStep(3);
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveStep3 = async (choice: 'build' | 'connect' | 'skip') => {
    setSaving(true);
    setError('');
    try {
      setWebsiteChoice(choice);
      setStep(4);
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const launchPipeline = async () => {
    if (!primaryGoal) {
      setError('Please select your primary goal');
      return;
    }
    setSaving(true);
    setError('');

    try {
      await apiClient.post('/onboarding/step3', { primary_goal: primaryGoal });
    } catch {
      setError('Failed to save goal. Please try again.');
      setSaving(false);
      return;
    }

    setSaving(false);
    setLaunching(true);
    setLaunchEvents([]);

    const token = localStorage.getItem('access_token') || '';
    const es = new EventSource(
      `${API_BASE_URL}/onboarding/launch?token=${encodeURIComponent(token)}`
    );
    eventSourceRef.current = es;

    es.onmessage = (event) => {
      try {
        const data: LaunchEvent = JSON.parse(event.data);

        if (data.step === 'complete' && data.status === 'complete') {
          setLaunchComplete(true);
          setLaunchProjectId(data.project_id || null);
          es.close();
          return;
        }

        if (data.step === 'error') {
          setError(data.message || 'An error occurred during setup.');
          es.close();
          return;
        }

        // Logo preview: pause progress bar, show approval card
        if (data.step === 'logo' && data.status === 'preview') {
          setLogoPreviewEvent(data);
          setLogoRegenerating(false);
          // Add a placeholder event so the logo step shows "progress"
          setLaunchEvents(prev => [...prev, data]);
          return;
        }

        // Logo finished (committed, skipped, or failed) — clear preview card
        if (data.step === 'logo' && (data.status === 'complete' || data.status === 'regenerating')) {
          if (data.status === 'complete') setLogoPreviewEvent(null);
          setLaunchEvents(prev => [...prev, data]);
          return;
        }

        setLaunchEvents(prev => [...prev, data]);
      } catch (err) {
        console.error('Failed to parse SSE event:', err);
      }
    };

    es.onerror = () => {
      es.close();
      if (!launchComplete) {
        setError('Connection lost — workspace setup was interrupted and may be incomplete. Please contact support so we can finish setting up your workspace.');
      }
    };
  };

  const goToDashboard = async () => {
    if (refreshToken) await refreshToken();
    navigate('/dashboard', { state: { refreshProjects: true } });
  };

  const DEFAULT_STREAM_NAMES = [
    'Industry Insights',
    'How-To Guides',
    'Customer Stories',
    'Product Updates',
    'Best Practices',
    'Market Trends',
  ];

  const runMasterplanGeneration = async () => {
    if (!launchProjectId) return;
    setMasterplanCreating(true);
    setMasterplanError(null);
    const placeholders = DEFAULT_STREAM_NAMES;
    setMasterplanStreams(placeholders);

    try {
      // Step 1: Create masterplan. MasterplanCreate requires ALL of these keys
      // (missing ones 422); the endpoint overrides project_id from the path.
      // Real wizard values beat empty strings for stream quality.
      const createRes = await apiClient.post(`/projects/${launchProjectId}/masterplan/create`, {
        project_id: launchProjectId,
        industry: industry || 'General',
        business_type: industry || 'General',
        primary_location: location || '',
        jurisdiction: location || '',
        country: location || '',
        num_streams: placeholders.length,
        target_audience: targetCustomer ? [targetCustomer] : null,
      });
      const masterplanId = createRes.data?.masterplan_id || createRes.data?.id;

      if (masterplanId) {
        // Step 2: Generate streams. Body is a BARE JSON array — the endpoint
        // declares `stream_names: List[str]` (same contract as api.ts
        // generateMasterplanStreams). Returned streams are dicts keyed
        // `stream_name`, not plain strings.
        //
        // 6 streams × Gemini ≈ 3–4 min of real server time, so the apiClient
        // default (120s) is not enough — use 300s to match nginx's
        // proxy_read_timeout ceiling. If the request STILL dies, generation
        // keeps running server-side and persists on completion, so fall back
        // to polling the masterplan's generation_status.
        const extractNames = (streams: any): string[] =>
          (Array.isArray(streams) ? streams : [])
            .map((s: any) => (typeof s === 'string' ? s : s?.stream_name || s?.name))
            .filter(Boolean);

        let names: string[] = [];
        try {
          const streamRes = await apiClient.post(
            `/masterplans/${masterplanId}/generate_streams`,
            placeholders,
            { timeout: 300000 },
          );
          names = extractNames(streamRes.data?.streams);
        } catch (genErr) {
          // Poll for up to 3 more minutes — server-side work outlives the request.
          for (let i = 0; i < 36; i++) {
            await new Promise(resolve => setTimeout(resolve, 5000));
            try {
              const mp = await apiClient.get(`/masterplans/${masterplanId}`);
              const status = mp.data?.generation_status;
              if (status === 'completed' || status === 'partial') {
                names = extractNames(mp.data?.streams);
                break;
              }
              if (status === 'error') break;
            } catch { /* transient — keep polling */ }
          }
          if (!names.length) throw genErr;
        }
        setMasterplanStreams(names.length ? names : placeholders);
      }
      setMasterplanComplete(true);
    } catch (err: any) {
      console.error('Masterplan generation failed:', err?.response?.data?.detail || err);
      setMasterplanError('Masterplan generation failed. You can still continue.');
      setMasterplanComplete(true); // allow Continue anyway
    } finally {
      setMasterplanCreating(false);
    }
  };

  const skip = async () => {
    try {
      await apiClient.post('/onboarding/skip', {});
      if (refreshToken) await refreshToken();
    } catch { /* non-fatal */ }
    navigate('/dashboard');
  };

  // ── Logo approval handlers ──

  const sendLogoDecision = async (action: 'confirm' | 'skip' | 'regenerate') => {
    setLogoDecisionLoading(true);
    if (action === 'regenerate') setLogoRegenerating(true);
    try {
      await apiClient.post('/onboarding/logo-decision', { action });
      if (action !== 'regenerate') setLogoPreviewEvent(null);
    } catch {
      // non-fatal — SSE will timeout and skip
    } finally {
      setLogoDecisionLoading(false);
    }
  };

  // ── Launch progress helpers ──

  const getStepStatus = (stepKey: string): 'pending' | 'progress' | 'complete' | 'error' => {
    // Logo step: if preview is showing, force "progress" so the spinner stays
    if (stepKey === 'logo' && logoPreviewEvent) return 'progress';

    const events = launchEvents.filter(e => e.step === stepKey);
    if (events.length === 0) {
      const stepIndex = LAUNCH_STEPS.findIndex(s => s.key === stepKey);
      const laterStarted = LAUNCH_STEPS.slice(stepIndex + 1).some(s =>
        launchEvents.some(e => e.step === s.key)
      );
      if (laterStarted) return 'complete';
      return 'pending';
    }

    const completeCount = events.filter(e => e.status === 'complete').length;
    const errorCount = events.filter(e => e.status === 'error').length;
    const last = events[events.length - 1];

    if (last.total && last.total > 1) {
      if (completeCount + errorCount >= last.total) return completeCount > 0 ? 'complete' : 'error';
      return 'progress';
    }

    if (last.status === 'complete') return 'complete';
    if (last.status === 'error') return 'error';
    return 'progress';
  };

  const getStepDetail = (stepKey: string): string => {
    const events = launchEvents.filter(e => e.step === stepKey);
    if (events.length === 0) return '';

    const last = events[events.length - 1];
    const completeCount = events.filter(e => e.status === 'complete').length;

    if (last.total && last.total > 1) {
      const status = getStepStatus(stepKey);
      if (status === 'progress') return `(${completeCount}/${last.total})`;
      if (status === 'complete') return `(${completeCount}/${last.total})`;
    }
    return '';
  };

  const getProgressPct = (): number => {
    const completedEvents = launchEvents.filter(e => e.status === 'complete').length;
    // Expected: project(1) + brand(1) + logo(1) + email_template(1) + topics(1) + articles(5) + social(5)
    //           + calendar(1) + quiz(1) + landing_page(1) + scheduling(1) = 19
    return Math.min(100, Math.round((completedEvents / 19) * 100));
  };

  const stepLabels = ['Your Business', 'Brand Identity', 'Your Website', 'Your Goal', 'Content Masterplan', 'Content Calendar'];

  return (
    <div className="onboarding-overlay">
      <div className={`onboarding-stage ${tutorialVisible ? 'tutorial-on' : 'tutorial-off'}`}>
      <div className="onboarding-card">
        {/* Header */}
        <div className="onboarding-header">
          <h1 className="onboarding-title">
            {step === 5
              ? 'Build Your Content Masterplan'
              : step === 6
              ? 'Generate Your First 30-Day Calendar'
              : launching
              ? (launchComplete ? 'Your workspace is ready!' : 'Setting up your workspace')
              : 'Welcome to AiMe'}
          </h1>
          <p className="onboarding-subtitle">
            {step === 5
              ? 'We\'ll generate 6 content streams for your business — this takes a few minutes.'
              : step === 6
              ? 'Review and approve your AI-generated 30-day content calendar.'
              : launching
              ? (launchComplete
                  ? 'Everything has been created. Head to your dashboard to explore.'
                  : logoPreviewEvent
                  ? 'Setup is paused — approve your logo below to continue.'
                  : "This takes about 7 minutes. We're creating everything you need.")
              : "Let's set up your marketing engine in 4 quick steps."}
          </p>
          {!launching && step <= 4 && <button className="onboarding-skip" onClick={skip}>Skip setup</button>}
        </div>

        {/* Progress dots — hide when launching; show on steps 5+6 */}
        {(!launching || step >= 5) && (
          <>
            <div className="onboarding-progress">
              {([1, 2, 3, 4, 5, 6] as Step[]).map((s) => (
                <React.Fragment key={s}>
                  <div className={`onboarding-step-dot ${s < step ? 'done' : s === step ? 'active' : ''}`}>
                    {s < step ? <Check size={12} /> : s}
                  </div>
                  {s < 6 && <div className={`onboarding-step-line ${s < step ? 'done' : ''}`} />}
                </React.Fragment>
              ))}
            </div>
            <div className="onboarding-step-labels">
              {stepLabels.map((l, i) => (
                <span key={i} className={i + 1 === step ? 'active' : ''}>{l}</span>
              ))}
            </div>
          </>
        )}

        {/* Error */}
        {error && <div className="onboarding-error">{error}</div>}

        {/* Step 1 — Your Business */}
        {step === 1 && (
          <div className="onboarding-body">
            <div className="onboarding-field">
              <label><Building2 size={14} /> Industry <span className="req">*</span></label>
              <select value={industry} onChange={e => setIndustry(e.target.value)} onFocus={() => setFocusedField('industry')} className="onboarding-select">
                <option value="">Select your industry…</option>
                {INDUSTRY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
              <FieldHelper field="industry" activeField={focusedField} />
            </div>
            <div className="onboarding-field">
              <label><MapPin size={14} /> Location / Market <span className="req">*</span></label>
              <input type="text" placeholder="e.g. Sydney, Australia" value={location} onChange={e => setLocation(e.target.value)} onFocus={() => setFocusedField('location')} className="onboarding-input" />
              <FieldHelper field="location" activeField={focusedField} />
            </div>
            <div className="onboarding-field">
              <label>
                <Users size={14} /> Who is your target customer? <span className="req">*</span>
                <SuggestButton
                  loading={suggestingField === 'targetCustomer'}
                  disabled={!industry}
                  onClick={() => requestSuggestion('targetCustomer')}
                />
              </label>
              <input type="text" placeholder="e.g. Small business owners aged 30–50" value={targetCustomer} onChange={e => setTargetCustomer(e.target.value)} onFocus={() => setFocusedField('targetCustomer')} className="onboarding-input" />
              <FieldHelper field="targetCustomer" activeField={focusedField} />
            </div>
            <div className="onboarding-field">
              <label>
                <Target size={14} /> Competitors (up to 3)
                <SuggestButton
                  loading={suggestingField === 'competitors'}
                  disabled={!industry}
                  onClick={() => requestSuggestion('competitors')}
                />
              </label>
              <div className="competitor-input-row">
                <input type="text" placeholder="Enter a competitor name" value={competitorInput} onChange={e => setCompetitorInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addCompetitor()} onFocus={() => setFocusedField('competitors')} className="onboarding-input" disabled={competitors.length >= 3} />
                <button onClick={addCompetitor} disabled={!competitorInput.trim() || competitors.length >= 3} className="competitor-add-btn">Add</button>
              </div>
              <div className="competitor-tags">
                {competitors.map(c => (
                  <span key={c} className="competitor-tag">{c}<button onClick={() => removeCompetitor(c)}><X size={10} /></button></span>
                ))}
              </div>
              <FieldHelper field="competitors" activeField={focusedField} />
            </div>

            {/* Contact info — optional but used for email template footers */}
            <div style={{ borderTop: '1px solid var(--stroke)', marginTop: '16px', paddingTop: '16px' }} onFocus={() => setFocusedField('companyContact')}>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                Contact details below are optional but will be used in your email template footers.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="onboarding-field" style={{ marginBottom: 0 }}>
                  <label><Mail size={14} /> Business Email</label>
                  <input type="email" placeholder="info@company.com" value={companyEmail} onChange={e => setCompanyEmail(e.target.value)} onFocus={() => setFocusedField('companyContact')} className="onboarding-input" />
                </div>
                <div className="onboarding-field" style={{ marginBottom: 0 }}>
                  <label><Phone size={14} /> Phone</label>
                  <input type="text" placeholder="(03) 1234 5678" value={companyPhone} onChange={e => setCompanyPhone(e.target.value)} onFocus={() => setFocusedField('companyContact')} className="onboarding-input" />
                </div>
              </div>
              <div className="onboarding-field" style={{ marginTop: '12px', marginBottom: 0 }}>
                <label><MapPinned size={14} /> Business Address</label>
                <input type="text" placeholder="123 Main St, Melbourne VIC 3000" value={companyAddress} onChange={e => setCompanyAddress(e.target.value)} onFocus={() => setFocusedField('companyContact')} className="onboarding-input" />
              </div>
              <FieldHelper field="companyContact" activeField={focusedField} />
            </div>
          </div>
        )}

        {/* Step 2 — Brand Identity */}
        {step === 2 && (
          <div className="onboarding-body">
            <div className="onboarding-field">
              <label><Building2 size={14} /> Brand / Business Name <span className="req">*</span></label>
              <input type="text" placeholder="e.g. Acme Marketing Co." value={brandName} onChange={e => setBrandName(e.target.value)} onFocus={() => setFocusedField('brandName')} className="onboarding-input" />
              <FieldHelper field="brandName" activeField={focusedField} />
            </div>
            <div className="onboarding-field">
              <label>
                <Palette size={14} /> Tell us about your business
                <SuggestButton
                  loading={suggestingField === 'brandDescription'}
                  disabled={!brandName || !industry}
                  onClick={() => requestSuggestion('brandDescription')}
                />
              </label>
              <textarea
                placeholder="e.g. A modern law firm focused on tax strategy and asset protection. We started in 2015 to help small business owners protect what they've built. We're passionate about making complex tax law accessible and affordable."
                value={brandDescription}
                onChange={e => setBrandDescription(e.target.value)}
                onFocus={() => setFocusedField('brandDescription')}
                className="onboarding-input"
                rows={3}
                style={{ resize: 'vertical', minHeight: '72px' }}
              />
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>How you started, what you're passionate about, what makes you different — used to generate your brand story and logo</span>
              <FieldHelper field="brandDescription" activeField={focusedField} />
            </div>
            <div className="onboarding-field">
              <label><Palette size={14} /> Brand Tone</label>
              <div className="tone-grid">
                {TONE_OPTIONS.map(t => (
                  <button key={t.value} className={`tone-btn ${brandTone === t.value ? 'selected' : ''}`} onClick={() => setBrandTone(t.value)}>
                    <strong>{t.label}</strong>
                    <span>{t.desc}</span>
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="onboarding-field">
                <label><Palette size={14} /> Primary Colour</label>
                <div className="color-row">
                  <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} onFocus={() => setFocusedField('brandColors')} className="onboarding-color-swatch" />
                  <span className="color-value">{primaryColor}</span>
                </div>
              </div>
              <div className="onboarding-field">
                <label><Palette size={14} /> Secondary Colour</label>
                <div className="color-row">
                  <input type="color" value={secondaryColor} onChange={e => setSecondaryColor(e.target.value)} onFocus={() => setFocusedField('brandColors')} className="onboarding-color-swatch" />
                  <span className="color-value">{secondaryColor}</span>
                </div>
              </div>
            </div>
            <FieldHelper field="brandColors" activeField={focusedField} />
            <div className="onboarding-field" onMouseEnter={() => setFocusedField('logo')}>
              <label><ImageIcon size={14} /> Logo (optional — we'll generate one if you don't have one)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {logoPreview ? (
                  <div style={{ width: '64px', height: '64px', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--panel-strong)' }}>
                    <img src={logoPreview} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  </div>
                ) : (
                  <div style={{ width: '64px', height: '64px', borderRadius: 'var(--radius-md)', border: '2px dashed var(--stroke)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', cursor: 'pointer' }} onClick={() => logoInputRef.current?.click()}>
                    <Upload size={20} />
                  </div>
                )}
                <div>
                  <button
                    onClick={() => logoInputRef.current?.click()}
                    style={{ padding: '6px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)', background: 'var(--panel)', color: 'var(--text)', cursor: 'pointer', fontSize: '0.85rem' }}
                  >
                    {logoFile ? 'Change Logo' : 'Upload Logo'}
                  </button>
                  {logoFile && (
                    <button onClick={() => { setLogoFile(null); setLogoPreview(''); }} style={{ marginLeft: '8px', padding: '6px 10px', border: 'none', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.8rem' }}>
                      Remove
                    </button>
                  )}
                </div>
                <input ref={logoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => setLogoFile(e.target.files?.[0] || null)} />
              </div>
              <FieldHelper field="logo" activeField={focusedField} />
            </div>

            {/* Logo style preferences — shown when no logo uploaded */}
            {!logoFile && (
              <div style={{ borderTop: '1px solid var(--stroke)', marginTop: '16px', paddingTop: '16px' }}>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                  No logo? We'll generate one. Pick a style:
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="onboarding-field" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.8rem' }}>Design Style</label>
                    <div className="tone-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)', gap: '6px' }}>
                      {LOGO_DESIGN_STYLES.map(o => (
                        <button key={o.value} className={`tone-btn ${logoDesignStyle === o.value ? 'selected' : ''}`} onClick={() => setLogoDesignStyle(o.value)} style={{ padding: '6px 4px', fontSize: '0.75rem' }}>
                          <strong>{o.label}</strong>
                          <span style={{ fontSize: '0.65rem' }}>{o.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="onboarding-field" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.8rem' }}>Type</label>
                    <div className="tone-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
                      {LOGO_COMPLEXITY.map(o => (
                        <button key={o.value} className={`tone-btn ${logoComplexity === o.value ? 'selected' : ''}`} onClick={() => setLogoComplexity(o.value)} style={{ padding: '6px 4px', fontSize: '0.75rem' }}>
                          <strong>{o.label}</strong>
                          <span style={{ fontSize: '0.65rem' }}>{o.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="onboarding-field" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.8rem' }}>Shape</label>
                    <div className="tone-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
                      {LOGO_SHAPES.map(o => (
                        <button key={o.value} className={`tone-btn ${logoShape === o.value ? 'selected' : ''}`} onClick={() => setLogoShape(o.value)} style={{ padding: '6px 4px', fontSize: '0.75rem' }}>
                          <strong>{o.label}</strong>
                          <span style={{ fontSize: '0.65rem' }}>{o.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="onboarding-field" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.8rem' }}>Colour Mode</label>
                    <div className="tone-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
                      {LOGO_COLOR_MODES.map(o => (
                        <button key={o.value} className={`tone-btn ${logoColorMode === o.value ? 'selected' : ''}`} onClick={() => setLogoColorMode(o.value)} style={{ padding: '6px 4px', fontSize: '0.75rem' }}>
                          <strong>{o.label}</strong>
                          <span style={{ fontSize: '0.65rem' }}>{o.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3 — Website choice */}
        {step === 3 && (
          <div className="onboarding-body">
            {!showWpPairing ? (
              <>
                <p className="goal-intro">
                  Do you have an existing WordPress website you'd like to connect, or would you like to build a new site from scratch?
                </p>
                <div className="website-choice-grid">
                  <button className={`website-choice-btn ${websiteChoice === 'build' ? 'selected' : ''}`} onClick={() => setWebsiteChoice('build')}>
                    <span className="website-choice-icon"><Hammer size={32} /></span>
                    <strong>Build a New Site</strong>
                    <span>Use AiMe's Site Builder — AI-generated from your brand guide. No existing site needed.</span>
                  </button>
                  <button className={`website-choice-btn ${websiteChoice === 'connect' ? 'selected' : ''}`} onClick={() => setWebsiteChoice('connect')}>
                    <span className="website-choice-icon"><Link2 size={32} /></span>
                    <strong>Connect Existing Site</strong>
                    <span>Already have an established WordPress website? Connect it to AiMe and publish directly from here.</span>
                  </button>
                </div>
                {wpConnectedSite && (
                  <p style={{ color: 'var(--success)', fontSize: '0.875rem', textAlign: 'center', marginTop: 8 }}>
                    Connected: {wpConnectedSite.site_url}
                  </p>
                )}
              </>
            ) : (
              <PairingWizard
                embedded={true}
                onConnected={(site) => { setWpConnectedSite(site); setShowWpPairing(false); setWebsiteChoice('connect'); }}
                onCancel={() => setShowWpPairing(false)}
              />
            )}
          </div>
        )}

        {/* Step 4 — Goal selection (before launch) */}
        {step === 4 && !launching && (
          <div className="onboarding-body">
            <p className="goal-intro">What's your primary marketing goal? We'll generate your first batch of content automatically.</p>
            <div className="goal-grid">
              {GOAL_OPTIONS.map(g => (
                <button key={g.value} className={`goal-btn ${primaryGoal === g.value ? 'selected' : ''}`} onClick={() => setPrimaryGoal(g.value)}>
                  <span className="goal-icon">{g.icon}</span>
                  <strong>{g.label}</strong>
                  <span>{g.desc}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 4 — Launch progress screen */}
        {step === 4 && launching && (
          <div className="onboarding-body launch-progress">
            {/* Logo approval card — ABOVE the steps list so it can't hide below
                the fold of the scrollable onboarding card. The SSE generator is
                paused server-side until this decision is made (or times out). */}
            {logoPreviewEvent && logoPreviewEvent.variants && (
              <div className="logo-approval-card" ref={logoApprovalRef}>
                <div className="logo-approval-header">
                  <strong>Your brand logo is ready — does it look good?</strong>
                  <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    {logoRegenerating ? 'Generating a new logo…' : 'Choose to use it, regenerate, or skip for now.'}
                  </p>
                </div>
                {logoRegenerating ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
                    <Loader2 size={32} className="launch-spin" style={{ color: 'var(--primary)' }} />
                  </div>
                ) : (
                  <>
                    <div className="logo-approval-variants">
                      <div className="logo-variant-cell logo-variant-light">
                        <img src={logoPreviewEvent.variants.primary} alt="Primary logo on light" />
                        <span>Primary</span>
                      </div>
                      <div className="logo-variant-cell logo-variant-dark">
                        <img src={logoPreviewEvent.variants.dark_bg} alt="Logo on dark background" />
                        <span>Dark bg</span>
                      </div>
                      <div className="logo-variant-cell logo-variant-light">
                        <img src={logoPreviewEvent.variants.mono_black} alt="Monochrome black" />
                        <span>Mono black</span>
                      </div>
                      <div className="logo-variant-cell logo-variant-dark">
                        <img src={logoPreviewEvent.variants.mono_white} alt="Monochrome white" />
                        <span>Mono white</span>
                      </div>
                    </div>
                    <div className="logo-approval-actions">
                      <button
                        className="onboarding-btn-primary"
                        disabled={logoDecisionLoading}
                        onClick={() => sendLogoDecision('confirm')}
                      >
                        {logoDecisionLoading ? <Loader2 size={14} className="launch-spin" /> : <Check size={14} />}
                        Use this logo
                      </button>
                      <button
                        className="onboarding-btn-secondary"
                        disabled={logoDecisionLoading}
                        onClick={() => sendLogoDecision('regenerate')}
                      >
                        Regenerate
                      </button>
                      <button
                        className="onboarding-btn-secondary"
                        disabled={logoDecisionLoading}
                        onClick={() => sendLogoDecision('skip')}
                      >
                        Skip — I'll upload my own
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="launch-steps">
              {LAUNCH_STEPS.map(ls => {
                const status = getStepStatus(ls.key);
                const detail = getStepDetail(ls.key);
                return (
                  <div key={ls.key} className={`launch-step launch-step-${status}`}>
                    <span className="launch-step-icon">
                      {status === 'complete' && <Check size={16} />}
                      {status === 'progress' && <Loader2 size={16} className="launch-spin" />}
                      {status === 'error' && <X size={16} />}
                      {status === 'pending' && <Circle size={16} />}
                    </span>
                    <span className="launch-step-label">
                      {ls.label}
                      {detail && <span className="launch-step-detail"> {detail}</span>}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Progress bar */}
            <div className="launch-progress-bar">
              <div className="launch-progress-fill" style={{ width: `${getProgressPct()}%` }} />
            </div>
            <div className="launch-progress-pct">{getProgressPct()}%</div>
          </div>
        )}

        {/* Step 5 — Masterplan generation */}
        {step === 5 && (
          <div className="onboarding-body">
            <div className="qs-masterplan-info">
              {masterplanCreating ? (
                <p className="qs-masterplan-desc">Generating your content streams — this takes a few minutes…</p>
              ) : masterplanError ? (
                <p className="qs-masterplan-error">{masterplanError}</p>
              ) : masterplanComplete ? (
                <p className="qs-masterplan-desc">Your masterplan streams are ready. Click Continue to generate your 30-day calendar.</p>
              ) : null}
            </div>
            <div className="qs-stream-list">
              {masterplanStreams.length === 0
                ? DEFAULT_STREAM_NAMES.map((name, i) => (
                    <div key={i} className="qs-stream-row qs-stream-skeleton">
                      <div className="qs-skeleton-shimmer" />
                    </div>
                  ))
                : masterplanStreams.map((name, i) => (
                    <div key={i} className={`qs-stream-row ${masterplanCreating ? 'qs-stream-skeleton' : 'qs-stream-complete'}`}>
                      {masterplanCreating ? <div className="qs-skeleton-shimmer" /> : <span className="qs-stream-name">{name}</span>}
                    </div>
                  ))
              }
            </div>
          </div>
        )}

        {/* Step 6 — Content plan wizard (inline) */}
        {step === 6 && launchProjectId && (
          <div className="onboarding-body qs-plan-body">
            <ContentPlanWizard
              isOpen={true}
              onClose={() => goToDashboard()}
              projectId={launchProjectId}
              inline={true}
              forceGeminiOnly={true}
              autoStart={true}
              initialSeeds={masterplanStreams.length > 0 ? masterplanStreams : DEFAULT_STREAM_NAMES}
              initialLocale="en-AU"
              initialCadence="3x_week"
              initialChannel="wordpress"
              endpointOverrides={{
                generate: '/api/onboarding/quickstart-generate-plan',
                approve: '/api/onboarding/quickstart-approve-plan',
                patch: '/api/onboarding/quickstart-patch-plan',
              }}
              onApproveSuccess={(_result) => {
                setQuickstartApproveSuccess(true);
                navigate(`/scheduling/${launchProjectId}`, { state: { celebration: true } });
              }}
            />
          </div>
        )}

        {/* Footer */}
        <div className="onboarding-footer">
          {step > 1 && !launching && step <= 4 && (
            <button className="onboarding-btn-back" onClick={() => { setError(''); setStep((step - 1) as Step); }}>
              <ChevronLeft size={16} /> Back
            </button>
          )}
          {step === 1 && <button className="onboarding-btn-primary" onClick={saveStep1} disabled={saving}>{saving ? 'Saving…' : <>Next <ChevronRight size={16} /></>}</button>}
          {step === 2 && <button className="onboarding-btn-primary" onClick={saveStep2} disabled={saving}>{saving ? 'Saving…' : <>Next <ChevronRight size={16} /></>}</button>}
          {step === 3 && !showWpPairing && (
            <>
              {websiteChoice === 'connect' && !wpConnectedSite && (
                <button className="onboarding-btn-primary" onClick={() => setShowWpPairing(true)}>
                  Connect Now <ChevronRight size={16} />
                </button>
              )}
              {(websiteChoice === 'build' || wpConnectedSite) && (
                <button className="onboarding-btn-primary" onClick={() => saveStep3(websiteChoice as 'build' | 'connect')} disabled={saving}>
                  {saving ? 'Saving…' : <>Next <ChevronRight size={16} /></>}
                </button>
              )}
              {!websiteChoice && (
                <button className="onboarding-btn-secondary" style={{ marginLeft: 'auto' }} onClick={() => saveStep3('skip')}>
                  Skip this step
                </button>
              )}
            </>
          )}
          {step === 4 && !launching && (
            <button className="onboarding-btn-primary" onClick={launchPipeline} disabled={saving || !primaryGoal}>
              {saving ? 'Saving…' : <>Let's Go! <Flag size={16} /></>}
            </button>
          )}
          {step === 4 && launching && launchComplete && (
            <>
              <button
                className="onboarding-btn-primary"
                onClick={() => {
                  setStep(5);
                  setLaunching(false);
                  setLaunchComplete(false);
                  runMasterplanGeneration();
                }}
              >
                Continue: Build Content Masterplan <ChevronRight size={16} />
              </button>
              <button
                className="onboarding-btn-secondary"
                style={{ marginLeft: '8px' }}
                onClick={goToDashboard}
              >
                Skip to Dashboard
              </button>
            </>
          )}
          {step === 5 && (
            <div className="onboarding-quickstart-footer">
              <button
                className="onboarding-btn-primary"
                disabled={!masterplanComplete}
                onClick={() => setStep(6)}
              >
                Continue <ChevronRight size={16} />
              </button>
              <button
                className="onboarding-btn-secondary"
                style={{ marginLeft: '8px' }}
                onClick={async () => {
                  await skipQuickstartStep('masterplan').catch(() => {});
                  goToDashboard();
                }}
              >
                Skip — I'll do this later
              </button>
            </div>
          )}
          {step === 6 && (
            <div className="onboarding-quickstart-footer">
              <button
                className="onboarding-btn-secondary"
                onClick={async () => {
                  await skipQuickstartStep('plan').catch(() => {});
                  goToDashboard();
                }}
              >
                Skip — I'll do this later
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tutorial sidecar — only on steps 1–4 (the wizard form). Skipped on
          launch screens and the masterplan/calendar steps so it doesn't get
          in the way of long-running progress UI. */}
      {tutorialVisible && step <= 4 && !launching && (
        <TutorialSidecar
          activeField={focusedField}
          onDismiss={toggleTutorial}
        />
      )}

      </div>

      {/* "Show tutorial" reopener — appears when dismissed, anchored to the
          overlay rather than the stage so it doesn't shift the modal. */}
      {!tutorialVisible && step <= 4 && !launching && (
        <button
          type="button"
          className="onboarding-tutorial-reopen"
          onClick={toggleTutorial}
          title="Show tutorial"
        >
          <Eye size={14} /> Show tutorial
        </button>
      )}

      {/* Step 5 — Masterplan generation */}
      {step === 5 && (
        <div className="onboarding-overlay" style={{ display: 'none' }} />
      )}

    </div>
  );
};

export default OnboardingWizard;
